/**
 * LÓGICA ÚNICA E CENTRALIZADA DE CÁLCULO DE HORÁRIOS
 * 
 * Esta função contém TODAS as regras de negócio para cálculo de horas.
 * Deve ser usada em TODOS os lugares para garantir consistência:
 * - Edição (Resumo de Totais)
 * - Visualização (Ver)
 * - Impressão
 * 
 * REGRA FERIADOS: Feriados contam como horas normais (×1) - igual às outras horas
 * 
 * REGRA FÉRIAS/BAIXA: 8h por dia (atualizado 13-05-2026)
 * REGRA GESTÃO/INTEGRAÇÃO/REUNIÃO/APOIO: horas reais do turno (ou fallback 8h para gestao sem horas)
 */

import { isFeriado } from './horarioUtils';

/**
 * Arredonda horas para múltiplos de 15 minutos (igual ao backend)
 * Regra: 0-10min→0, 11-22min→15, 23-37min→30, 38-52min→45, 53-60min→60
 */
const arredondar15min = (horas) => {
  const negativo = horas < 0;
  const absValor = Math.abs(horas);
  const horasInt = Math.floor(absValor);
  const minutosDecimal = (absValor - horasInt) * 60;
  
  let minutosArredondados;
  if (minutosDecimal <= 10) {
    minutosArredondados = 0;
  } else if (minutosDecimal <= 22) {
    minutosArredondados = 15;
  } else if (minutosDecimal <= 37) {
    minutosArredondados = 30;
  } else if (minutosDecimal <= 52) {
    minutosArredondados = 45;
  } else {
    minutosArredondados = 60;
  }
  
  let resultado;
  if (minutosArredondados === 60) {
    resultado = horasInt + 1;
  } else {
    resultado = horasInt + (minutosArredondados / 60);
  }
  
  return negativo ? -resultado : resultado;
};

// Constantes de horas por dia
const HORAS_POR_DIA_FERIAS = 8;  // 8h (regra 13-05-2026)
const HORAS_POR_DIA_BAIXA = 8;   // 8h (regra 13-05-2026)
const HORAS_POR_DIA_GESTAO = 8;  // 8h (fallback se gestao sem horas)
const HORAS_POR_DIA_FOLGA_COMP = 8;  // 8h (folga de compensação)

/**
 * Calcula todas as métricas de horário para um colaborador
 * ESTA É A FUNÇÃO CENTRAL - USE EM TODOS OS LUGARES
 * 
 * @param {Object} colaborador - Objeto do colaborador com registos_por_dia
 * @param {number} semanas - Número de semanas do período
 * @param {number} bancoAnteriorParam - Banco de horas do mês anterior (opcional, usa do colaborador se não fornecido)
 * @param {number} anoReferencia - Ano do período para calcular multiplicador de feriados (opcional)
 * @returns {Object} Objeto com todas as métricas calculadas
 */
export const calcularMetricasHorario = (colaborador, semanas, bancoAnteriorParam = null, anoReferencia = null) => {
  const registosPorDia = colaborador.registos_por_dia || {};
  
  // Usar banco anterior do parâmetro ou do colaborador
  const bancoAnterior = bancoAnteriorParam !== null ? bancoAnteriorParam : (colaborador.banco_horas_anterior || 0);
  
  // Inicializar contadores
  let totalHorasNormais = 0;  // Horas de turnos normais (não feriado)
  let totalHorasFeriado = 0;  // Horas trabalhadas em feriado (para mostrar separado)
  let totalHorasFerias = 0;   // Horas de férias (8h por dia)
  let totalHorasReunioes = 0; // Horas de reuniões
  let totalHorasGestao = 0;   // Horas de gestão (horas reais OU 8h fallback)
  let totalHorasBaixa = 0;    // Horas de baixa (8h por dia)
  let totalHorasIntegracao = 0; // Horas de integração (horas reais do turno)
  
  // Conjuntos para contar dias únicos
  const diasComFerias = new Set();
  const diasComFalta = new Set();
  const diasComFolga = new Set();
  const diasComFolgaComp = new Set();  // Folga Compensação (conta 8h)
  const diasComBaixa = new Set();
  const diasComGestao = new Set();
  const diasGestaoInteiro = new Set(); // Gestão sem horário → 8h por dia
  
  // Contadores de turnos
  let totalManhas = 0;
  let totalTardes = 0;
  let turnosApoio = 0;
  
  // Detectar actividade do colaborador no período
  const temActividade = Object.values(registosPorDia).some(
    ts => Array.isArray(ts) && ts.some(t => t && ['normal', 'troca', 'apoio', 'reuniao', 'integracao', 'gestao'].includes(t.tipo))
  );

  // Processar cada dia
  Object.entries(registosPorDia).forEach(([dia, turnos]) => {
    if (!Array.isArray(turnos)) return;

    // Heurística: dia com array vazio + colaborador activo = FOLGA
    if (turnos.length === 0 && temActividade) {
      diasComFolga.add(dia);
      return;
    }

    const ehFeriado = isFeriado(dia);
    
    turnos.forEach(turno => {
      const tipo = turno.tipo;
      const horas = parseFloat(turno.horas) || 0;
      
      // Marcar dias únicos para tipos especiais
      if (tipo === 'ferias') diasComFerias.add(dia);
      if (tipo === 'falta') diasComFalta.add(dia);
      if (tipo === 'folga') diasComFolga.add(dia);
      if (tipo === 'folga_compensacao') {
        // Folga compensação: conta no bloco de folgas mas alimenta 8h
        diasComFolga.add(dia);
        diasComFolgaComp.add(dia);
      }
      // Gestão sem entrada/saída = dia inteiro (8h por dia único); com horário = horas reais
      const gestaoDiaInteiro = tipo === 'gestao' && !(turno.entrada && turno.saida);
      if (tipo === 'gestao') diasComGestao.add(dia);
      if (gestaoDiaInteiro) diasGestaoInteiro.add(dia);
      if (tipo === 'baixa') diasComBaixa.add(dia);
      if (tipo === 'apoio') turnosApoio++;
      
      // Contabilizar horas por tipo
      if (tipo === 'normal' || tipo === 'troca' || tipo === 'apoio' || tipo === 'integracao') {
        if (ehFeriado) {
          totalHorasFeriado += horas;
        } else {
          totalHorasNormais += horas;
        }
        // Integração também alimenta breakdown separado
        if (tipo === 'integracao') {
          totalHorasIntegracao += horas;
        }
        
        // Contar manhãs e tardes (turnos com 2h ou mais)
        if (turno.entrada && horas >= 2) {
          const [hora] = turno.entrada.split(':').map(Number);
          if (hora >= 5 && hora < 12) {
            totalManhas++;
          } else if (hora >= 12 && hora < 20) {
            totalTardes++;
          }
        }
      } else if (tipo === 'reuniao') {
        totalHorasReunioes += parseFloat(turno.horas || 1);
      } else if (tipo === 'gestao' && !gestaoDiaInteiro && horas > 0) {
        // Gestão com horas reais conta como horas normais agregadas
        totalHorasGestao += horas;
      }
    });
  });
  
  // Calcular horas por dias únicos
  const diasFerias = diasComFerias.size;
  const diasFalta = diasComFalta.size;
  const diasFolga = diasComFolga.size;
  const diasFolgaComp = diasComFolgaComp.size;
  const diasBaixa = diasComBaixa.size;
  const diasGestao = diasComGestao.size;
  // Dias de gestão SEM horas reais → usam fallback 8h
  const diasGestaoFallback = diasGestaoInteiro.size;

  totalHorasFerias = diasFerias * HORAS_POR_DIA_FERIAS;  // Cada dia de férias = 8h
  // Gestão: horas reais (já somadas acima) + 8h por cada dia sem horas
  totalHorasGestao = totalHorasGestao + (diasGestaoFallback * HORAS_POR_DIA_GESTAO);
  totalHorasBaixa = diasBaixa * HORAS_POR_DIA_BAIXA;     // Cada dia de baixa = 8h
  const totalHorasFolgaComp = diasFolgaComp * HORAS_POR_DIA_FOLGA_COMP; // Cada FC = 8h
  
  // ====== REGRAS DE CÁLCULO ======
  
  // FERIADOS: Contam como horas normais (×1)
  // Não há multiplicador especial para feriados
  const multiplicadorFeriado = 1;
  
  // Ano para cálculo (usado para referência)
  const anoCalculo = anoReferencia || new Date().getFullYear();
  
  // TOTAL H EXIBIDO: Soma de todas as horas
  // Normal + Feriado + Férias + Gestão + Baixa + Reuniões + Folga Compensação
  const totalHoras = totalHorasNormais + totalHorasFeriado + totalHorasFerias + totalHorasGestao + totalHorasBaixa + totalHorasReunioes + totalHorasFolgaComp;

  // TOTAL PARA BANCO: Feriados contam igual às horas normais (×1)
  // Normal + Feriado + Férias + Gestão + Baixa + Reuniões + Folga Compensação
  const totalParaBanco = totalHorasNormais + totalHorasFeriado + totalHorasFerias + totalHorasGestao + totalHorasBaixa + totalHorasReunioes + totalHorasFolgaComp;
  
  // Horas esperadas = semanas × 40h
  const horasEsperadas = semanas * 40;
  
  // Banco do período = Trabalhadas - Esperadas (arredondado para 15min)
  const bancoDesteMesRaw = totalParaBanco - horasEsperadas;
  const bancoDesteMes = arredondar15min(bancoDesteMesRaw);
  
  // Banco Total = Anterior + Este Mês (arredondado para 15min)
  const bancoTotalRaw = bancoAnterior + bancoDesteMes;
  const bancoTotal = arredondar15min(bancoTotalRaw);
  
  return {
    // Horas detalhadas (para breakdown)
    totalHorasNormais,
    totalHorasFeriado,
    totalHorasFerias,
    totalHorasReunioes,
    totalHorasGestao,
    totalHorasBaixa,
    totalHorasIntegracao,
    
    // Total para exibição
    totalHoras,
    
    // Total para cálculo do banco (com feriados conforme multiplicador do ano)
    totalParaBanco,
    
    // Dias únicos
    diasFerias,
    diasFalta,
    diasFolga,
    diasFolgaComp,
    diasBaixa,
    diasGestao,
    turnosApoio,
    
    // Turnos
    totalManhas,
    totalTardes,
    
    // Banco de horas
    horasEsperadas,
    bancoAnterior,
    bancoDesteMes,
    bancoTotal,
    
    // Multiplicador de feriados (para exibição)
    multiplicadorFeriado,
    anoCalculo,
    
    // Para debug
    breakdown: {
      normais: totalHorasNormais,
      feriados: totalHorasFeriado,
      feriadosComMultiplicador: totalHorasFeriado * multiplicadorFeriado,
      multiplicadorFeriado,
      ferias: totalHorasFerias,
      gestao: totalHorasGestao,
      baixa: totalHorasBaixa,
      reunioes: totalHorasReunioes,
      totalExibido: totalHoras,
      totalBanco: totalParaBanco,
      esperadas: horasEsperadas,
      bancoMes: bancoDesteMes,
      bancoFinal: bancoTotal
    }
  };
};

/**
 * Função auxiliar para calcular métricas apenas com os registos
 * Útil para quando não temos o objeto colaborador completo
 * 
 * @param {Object} registosPorDia - Registos por dia
 * @param {number} semanas - Número de semanas do período
 * @param {number} bancoAnterior - Banco de horas anterior
 * @param {number} anoReferencia - Ano do período para calcular multiplicador de feriados
 */
export const calcularMetricasRegistos = (registosPorDia, semanas, bancoAnterior = 0, anoReferencia = null) => {
  return calcularMetricasHorario({ registos_por_dia: registosPorDia, banco_horas_anterior: bancoAnterior }, semanas, null, anoReferencia);
};

/**
 * Recalcula métricas usando valores já salvos do backend
 * Apenas recalcula o "totalHoras" para exibição
 * 
 * @param {Object} colaborador - Colaborador com valores do backend
 * @param {number} anoReferencia - Ano do período para calcular multiplicador de feriados
 * @returns {Object} Métricas ajustadas para exibição
 */
export const ajustarMetricasBackend = (colaborador, anoReferencia = null) => {
  const totalHorasBackend = colaborador.total_horas || 0;
  const horasFeriado = colaborador.horas_feriado || 0;
  const diasFerias = colaborador.dias_ferias || 0;
  const diasBaixa = colaborador.dias_baixa || 0;
  const horasFerias = diasFerias * HORAS_POR_DIA_FERIAS;  // 8h por dia
  const horasBaixa = diasBaixa * HORAS_POR_DIA_BAIXA;     // 8h por dia
  
  // Determinar ano para cálculo (referência apenas)
  const anoCalculo = anoReferencia || new Date().getFullYear();
  
  // REGRA ATUALIZADA: Feriados contam como hora normal (×1) - SEM MULTIPLICADOR
  const multiplicadorFeriado = 1;
  
  // Calcular horas normais (reverso)
  const horasNormais = totalHorasBackend - horasFeriado - horasFerias - horasBaixa;
  
  // Total para exibição (feriado × 1, sem multiplicador especial)
  const totalHorasMostrado = horasNormais + horasFeriado + horasFerias + horasBaixa;
  
  return {
    totalHorasNormais: horasNormais,
    totalHorasFeriado: horasFeriado,
    totalHorasFerias: horasFerias,
    totalHorasBaixa: horasBaixa,
    totalHoras: totalHorasMostrado,
    diasFerias: diasFerias,
    diasFalta: colaborador.dias_falta || 0,
    diasFolga: colaborador.dias_folga || 0,
    diasBaixa: diasBaixa,
    bancoAnterior: colaborador.banco_horas_anterior || 0,
    bancoTotal: colaborador.banco_horas || 0,
    multiplicadorFeriado,
    anoCalculo
  };
};
